import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  productManifestApi,
  Shelf,
  ShelfRow,
  ShelfRowBay,
  ShelfRowFloor,
  ShelfRowPallet,
  CreateShelfRowPayload,
  UpdateShelfRowPayload,
  CreateShelfRowBayBinPayload,
  UpdateShelfRowBayBinPayload,
  CreateShelfRowFloorPayload,
  UpdateShelfRowFloorPayload,
  CreateShelfRowPalletPayload,
  UpdateShelfRowPalletPayload,
  BulkOperationPayload,
  AddLevelInBayPayload,
  UpdateShelfRowBayPayload,
  UpdateShelfBayLevelNamePayload,
  UpdateManifestPayload,
  Manifest,
} from "../apis/productManifestApi";

interface SplitManifestPayload {
  selectedProductIds: string[];
  requestType?: string;
  vehicleType?: string;
  locationId?: string;
  pickupDate?: string;
  timeSlot?: string;
  packageType?: string;
  products?: Array<{
    productId: string;
    quantity?: any;
    missingQty?: number;
    damageQty?: number;
    clientReason?: string;
    adminReason?: string;
    note?: string;
    reservedQuantity?: number;
  }>;
}

// Validation schemas for JSON payloads
export const CreateShelfPayloadSchema = z.object({
  warehouseId: z.string().min(1, "Warehouse ID is required"),
  storageType: z.enum(["B2B", "B2C"]), // 🔥 strict validation
  name: z.string().optional(),
});

export const UpdateManifestPayloadSchema = z
  .object({
    requestType: z.string().optional(),
    locationId: z.string().optional(), // Made optional to allow omission
    vehicleType: z.string().optional(), // Made optional to allow omission
    pickupDate: z.string().min(1, "Date is required"),
    timeSlot: z.string().min(1, "Time is required"),
    packageType: z.string().optional(),
    reqType: z.string().optional(),
    products: z
      .array(
        z.object({
          productId: z.string().min(1, "Sku is required"),
          quantity: z.number().min(1, "Quantity is required"),
          missingQty: z.number().nullable().optional(),
          damageQty: z.number().nullable().optional(),
          receivedQty: z.number().nullable().optional(),
          reason: z.string().nullable().optional(),
          clientReason: z.string().nullable().optional(),
        })
      )
      .min(1, "At least one Product is required"),
  })
  // 1. Validate based on requestType
  .superRefine((data, ctx) => {
    if (data.requestType !== "dropRequest" && data.requestType !== "DROP") {
      if (!data.locationId || data.locationId.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Location ID is required",
          path: ["locationId"],
        });
      }
      if (!data.vehicleType || data.vehicleType.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Vehicle Type is required",
          path: ["vehicleType"],
        });
      }
    }
  })
  // 2. Automatically remove the keys if it's a dropRequest
  .transform((data) => {
    if (data.requestType === "dropRequest") {
      const { locationId, vehicleType, ...rest } = data;
      return rest;
    }
    return data;
  });

export type CreateRequestForm = z.infer<typeof UpdateManifestPayloadSchema>;

export const CreateShelfRowPayloadSchema = z.object({
  shelveId: z.string().min(1, "Shelve ID is required"),
  warehouseId: z.string().min(1, "Warehouse ID is required"),

  bayLevelNumbers: z.string().min(1, "Bay Level Numbers is required"),
  bayLevelType: z.string().min(1, "Bay Level Type is required"),

  bayNumbers: z.string().min(1, "Bay Numbers is required"),
  bayType: z.string().min(1, "Bay Type is required"),

  gapType: z.string().min(1, "Gap Type is required"),

  rowNumbers: z.string().min(1, "Row Numbers is required"),
  rowType: z.string().min(1, "Row Type is required"),

  storageType: z.enum(["SHELVES", "FLOOR"]), // 🔥 strict validation
  name: z.string().optional(),
});

const UpdateShelfRowPayloadSchema = z.object({
  name: z.string().optional(),
  isActive: z.boolean().optional(),
});

const CreateShelfRowBayBinPayloadSchema = z.object({
  bayId: z.string().min(1, "Bay ID is required"),
  warehouseId: z.string().min(1, "Warehouse ID is required"),
  shelveId: z.string().min(1, "Shelf ID is required"),
  rowInstanceId: z.string().min(1, "Row Instance ID is required"),
  clientId: z.string().min(1, "Client ID is required"),
  storageType: z.enum(["B2C", "B2B"]),
  bins: z
    .array(
      z.object({
        level: z.string().min(1, "Level is required"),
        binType: z.string().min(1, "Bin Type is required"),
        binNumbers: z.string().min(1, "Bin Number is required"),
      })
    )
    .min(1, "At least one bin is required"),
});

const UpdateShelfRowBayBinPayloadSchema = z.object({
  name: z.string().optional(),
  id: z.string().min(1, "Bin ID is required"),
});

export const UpdateShelfNamePayloadSchema = z.object({
  name: z.string().optional(),
});

export const UpdateShelfBayLevelNamePayloadSchema = z.object({
  id: z.string().min(1, "Level ID is required"),
  name: z.string().optional(),
});

const CreateShelfRowFloorPayloadSchema = z.object({
  warehouseId: z.string().min(1, "Warehouse ID is required"),
  rowId: z.string().min(1, "Row ID is required"),
  shelveId: z.string().min(1, "Shelf ID is required"),
  rowInstanceId: z.string().min(1, "Row Instance ID is required"),
  createdBy: z.string().min(1, "Client ID is required"),
  storageType: z.enum(["PALLETS", "FLOOR"]),
  floorSlotPrefix: z.string().min(1, "Floor Slot Name is required"),
  floorSlotNumber: z.string().min(1, "Floor Slot Number is required"),
});

const UpdateShelfRowFloorPayloadSchema = z.object({
  name: z.string().optional(),
  id: z.string().min(1, "Floor ID is required"),
});

export const CreateShelfRowPalletPayloadSchema = z.discriminatedUnion(
  "palletType",
  [
    // 👉 Shelves Pallet Payload
    z.object({
      palletType: z.literal("SHELVES"),
      rowId: z.string().min(1, "Row ID is required"),
      bayId: z.string().min(1, "Bay ID is required"),
      warehouseId: z.string().min(1, "Warehouse ID is required"),
      shelveId: z.string().min(1, "Shelf ID is required"),
      rowInstanceId: z.string().min(1, "Row Instance ID is required"),
      clientId: z.string().min(1, "Client ID is required"),
      shelveLevels: z
        .array(
          z.object({
            numberOfPallets: z.string().optional(), // optional if coming from DB
            level: z.string().optional(),
            selected: z.boolean(),
          })
        )
        .min(1, "At least one level must be selected"),
    }),

    // 👉 Floor Pallet Payload
    z.object({
      palletType: z.literal("FLOOR"),
      rowId: z.string().min(1, "Row ID is required"),
      // bayId: z.string().min(1, "Bay ID is required"),
      warehouseId: z.string().min(1, "Warehouse ID is required"),
      shelveId: z.string().min(1, "Shelf ID is required"),
      rowInstanceId: z.string().min(1, "Row Instance ID is required"),
      clientId: z.string().min(1, "Client ID is required"),
      palletName: z.string().min(1, "Pallet Name is required"),
      numberOfPallets: z
        .string()
        .min(1, "Number of pallets must be at least 1"),
      floorPalletPrefix: z.string().min(1, "Floor Pallet Prefix is required"),
    }),
  ]
);

const UpdateShelfRowPalletPayloadSchema = z.object({
  name: z.string().optional(),
  id: z.string().min(1, "Pallet ID is required"),
});

const BulkOperationPayloadSchema = z.object({
  ids: z.array(z.string().min(1)).min(1, "At least one ID is required"),
  isActive: z.boolean().optional(),
});

// Corrected: Renamed `createMutation` to `useCreateMutation`
const useCreateMutation = <T, P>(
  mutationFn: (payload: P, isFormData?: boolean) => Promise<any>,
  successMessage: string,
  invalidateKeys: string[] = [],
  validator?: z.ZodSchema<P>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: P) => {
      if (validator) {
        validator.parse(payload);
      }
      const res = await mutationFn(payload);
      if (!res.success) {
        throw new Error(res.message || `${successMessage} failed`);
      }
      return res.data;
    },
    onSuccess: (data: T) => {
      console.log(`✅ ${successMessage}:`, data);
      invalidateKeys.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      );
    },
    onError: (error: Error) => {
      console.error(`❌ ${successMessage} failed:`, error.message);
    },
  });
};

// Corrected: The custom hook now takes the 'id' as a parameter directly.
const useCreateIdBasedMutation = <T, P>(
  mutationFn: (id: string, payload: P, isFormData?: boolean) => Promise<any>,
  successMessage: string,
  invalidateKeys: string[] = [],
  validator?: z.ZodSchema<P>,
  id?: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: P) => {
      if (!id) {
        throw new Error("ID is required");
      }
      if (validator) {
        validator.parse(payload);
      }
      const res = await mutationFn(id, payload);
      if (!res.success) {
        throw new Error(res.message || `${successMessage} failed`);
      }
      return res.data;
    },
    onSuccess: (data: T) => {
      console.log(`✅ ${successMessage}:`, data);
      invalidateKeys.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      );
    },
    onError: (error: Error) => {
      console.error(`❌ ${successMessage} failed:`, error.message);
    },
  });
};

export const useManifestCreateMutation = () =>
  useCreateMutation<Manifest, UpdateManifestPayload>(
    productManifestApi.createManifest,
    "Manifest created successfully",
    ["manifestList", "manifestAllData"],
    UpdateManifestPayloadSchema
  );

export const useShelfCreateFormDataMutation = () =>
  useCreateMutation<Shelf, Record<string, any>>(
    (payload: any) => productManifestApi.createManifest(payload, true), // Correctly pass the isFormData flag
    "Shelf created successfully (FormData)",
    ["shelfList", "shelfAllData"]
  );

export const useProductManifestListData = (
  page: number,
  limit: number,
  searchFor: string
) => {
  return useQuery({
    queryKey: ["productManifestList", page, limit, searchFor],
    queryFn: async () => {
      const res = await productManifestApi.getManifests(page, limit, searchFor);
      if (!res.success) {
        throw new Error(res.message || "Failed to fetch Product Manifests");
      }
      return res.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useFetchProductsAllData = () => {
  return useQuery({
    queryKey: ["productsAllData"],
    queryFn: async () => {
      const res = await productManifestApi.getAllProducts();
      if (!res.success) {
        throw new Error(res.message || "Failed to fetch products");
      }
      return res.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useFetchManifestById = (id: string) => {
  return useQuery({
    queryKey: ["manifest", id],
    queryFn: async () => {
      const res = await productManifestApi.getManifestById(id);
      if (!res.success) {
        throw new Error(res.message || "Failed to fetch manifest");
      }
      return res.data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useManifestUpdateMutation = (id: string) =>
  useCreateIdBasedMutation<Manifest, UpdateManifestPayload>(
    productManifestApi.updateManifest,
    "Manifest updated successfully",
    ["manifestList", "manifest"],
    UpdateManifestPayloadSchema,
    id
  );

export const useSplitManifestMutation = (parentId: string) =>
  useCreateIdBasedMutation<
    { parent: any; child: any },
    SplitManifestPayload
  >(
    (id, payload) => productManifestApi.splitManifest(id, payload),
    "Manifest split successfully",
    ["productManifestList", "manifest"],
    undefined,
    parentId
  );

export const useShelfUpdateFormDataMutation = (id: string) =>
  useCreateIdBasedMutation<Shelf, Record<string, any>>(
    (id, payload) => productManifestApi.updateShelfFormData(id, payload), // Correctly pass the isFormData flag
    "Shelf updated successfully (FormData)",
    ["shelfList", "shelfAllData", "shelf"],
    undefined,
    id
  );

export const useShelfDeleteMutation = (id: string) =>
  useCreateIdBasedMutation<void, Record<string, any>>(
    productManifestApi.deleteShelf,
    "Shelf deleted successfully",
    ["shelfList", "shelfAllData"],
    undefined,
    id
  );

export const useShelfBulkUpdateMutation = () =>
  useCreateMutation<void, BulkOperationPayload>(
    productManifestApi.bulkUpdateShelves,
    "Shelves bulk updated successfully",
    ["shelfList", "shelfAllData"],
    BulkOperationPayloadSchema
  );

export const useShelfRowCreateMutation = () =>
  useCreateMutation<ShelfRow, CreateShelfRowPayload>(
    productManifestApi.createShelfRow,
    "Shelf row created successfully",
    ["shelfRowsAllData"],
    CreateShelfRowPayloadSchema
  );

export const useShelfRowCreateFormDataMutation = () =>
  useCreateMutation<ShelfRow, Record<string, any>>(
    (payload: any) => productManifestApi.createShelfRow(payload, true), // Correctly pass the isFormData flag
    "Shelf row created successfully (FormData)",
    ["shelfRowsAllData"]
  );

export const useFetchShelfRowsAllData = (
  shelfId: string,
  warehouseId: string
) => {
  return useQuery({
    queryKey: ["shelfRowsAllData", shelfId, warehouseId],
    queryFn: async () => {
      const res = await productManifestApi.getShelfRows(shelfId, warehouseId);
      if (!res.success) {
        return [];
      }
      return res.data;
    },
    enabled: !!shelfId && !!warehouseId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useFetchShelfRowById = (id: string) => {
  return useQuery({
    queryKey: ["shelfRow", id],
    queryFn: async () => {
      const res = await productManifestApi.getShelfRowById(id);
      if (!res.success) {
        throw new Error(res.message || "Failed to fetch shelf row");
      }
      return res.data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useShelfRowUpdateMutation = (id: string) =>
  useCreateIdBasedMutation<ShelfRow, UpdateShelfRowPayload>(
    productManifestApi.updateShelfRow,
    "Shelf row updated successfully",
    ["shelfRowsAllData", "shelfRow"],
    UpdateShelfRowPayloadSchema,
    id
  );

export const useShelfRowUpdateFormDataMutation = (id: string) =>
  useCreateIdBasedMutation<ShelfRow, Record<string, any>>(
    (id, payload) => productManifestApi.updateShelfRowFormData(id, payload), // Correctly pass the isFormData flag
    "Shelf row updated successfully (FormData)",
    ["shelfRowsAllData", "shelfRow"],
    undefined,
    id
  );

export const useShelfRowDeleteMutation = (id: string) =>
  useCreateIdBasedMutation<void, Record<string, any>>(
    productManifestApi.deleteShelfRow,
    "Shelf row deleted successfully",
    ["shelfRowsAllData"],
    undefined,
    id
  );

export const useShelfRowBulkUpdateMutation = () =>
  useCreateMutation<void, BulkOperationPayload>(
    productManifestApi.bulkUpdateShelfRows,
    "Shelf rows bulk updated successfully",
    ["shelfRowsAllData"],
    BulkOperationPayloadSchema
  );

export const useShelfRowBayBinCreateMutation = () =>
  useCreateMutation<ShelfRowBay, CreateShelfRowBayBinPayload>(
    productManifestApi.createShelfRowBayBin,
    "Shelf row bay bin created successfully",
    ["shelfRowBayBins"],
    CreateShelfRowBayBinPayloadSchema
  );

export const useShelfRowBayBinCreateFormDataMutation = () =>
  useCreateMutation<ShelfRowBay, Record<string, any>>(
    (payload: any) => productManifestApi.createShelfRowBayBin(payload, true), // Correctly pass the isFormData flag
    "Shelf row bay bin created successfully (FormData)",
    ["shelfRowBayBins"]
  );

export const useFetchShelfRowBayBins = (rowId: string) => {
  return useQuery({
    queryKey: ["shelfRowBayBins", rowId],
    queryFn: async () => {
      const res = await productManifestApi.getShelfRowBayBins(rowId);
      if (!res.success) {
        return [];
      }
      return res.data;
    },
    enabled: !!rowId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useFetchShelfRowBayBinById = (id: string) => {
  return useQuery({
    queryKey: ["shelfRowBayBin", id],
    queryFn: async () => {
      const res = await productManifestApi.getShelfRowBayBinById(id);
      if (!res.success) {
        throw new Error(res.message || "Failed to fetch shelf row bay bin");
      }
      return res.data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useShelfRowBayBinUpdateMutation = (id: string) =>
  useCreateIdBasedMutation<ShelfRowBay, UpdateShelfRowBayBinPayload>(
    productManifestApi.updateShelfRowBayBin,
    "Shelf row bay bin updated successfully",
    ["shelfRowBayBins", "shelfRowBayBin"],
    UpdateShelfRowBayBinPayloadSchema,
    id
  );

export const useShelfRowBayBinUpdateFormDataMutation = (id: string) =>
  useCreateIdBasedMutation<ShelfRowBay, Record<string, any>>(
    (id, payload) =>
      productManifestApi.updateShelfRowBayBinFormData(id, payload),
    "Shelf row bay bin updated successfully (FormData)",
    ["shelfRowBayBins", "shelfRowBayBin"],
    undefined,
    id
  );

export const useShelfRowBayUpdateMutation = (id: string) =>
  useCreateIdBasedMutation<ShelfRowBay, UpdateShelfRowBayPayload>(
    productManifestApi.updateShelfRowBay,
    "Shelf row bay updated successfully",
    ["shelfRowBayUpdate", id],
    UpdateShelfNamePayloadSchema,
    id
  );

export const useShelfRowBayDeleteMutation = (id: string) =>
  useCreateIdBasedMutation<void, Record<string, any>>(
    productManifestApi.deleteShelfRowBay,
    "Shelf row bay deleted successfully",
    ["shelfRowBay"],
    undefined,
    id
  );

export const useShelfRowBayAddLevelMutation = (id: string) =>
  useCreateIdBasedMutation<ShelfRowBay, AddLevelInBayPayload>(
    productManifestApi.addShelfRowBayLevel,
    "Shelf row bay level added successfully",
    ["addShelfRowBayLevel", id],
    undefined,
    id
  );

export const useShelfRowBayUpdateLevelMutation = (bayId: string) =>
  useCreateIdBasedMutation<ShelfRowBay, UpdateShelfBayLevelNamePayload>(
    productManifestApi.updateShelfRowBayLevel,
    "Shelf row bay level updated successfully",
    ["updateShelfRowBayLevel"],
    UpdateShelfBayLevelNamePayloadSchema,
    bayId
  );

export const useShelfRowBayLevelDeleteMutation = (id: string) =>
  useCreateIdBasedMutation<void, Record<string, any>>(
    productManifestApi.deleteShelfRowBayLevel,
    "Shelf row bay level deleted successfully",
    ["shelfRowBayLevel"],
    undefined,
    id
  );

export const useShelfRowBayBinDeleteMutation = (id: string) =>
  useCreateIdBasedMutation<void, Record<string, any>>(
    productManifestApi.deleteShelfRowBayBin,
    "Shelf row bay bin deleted successfully",
    ["shelfRowBayBins"],
    undefined,
    id
  );

export const useShelfRowBayBinBulkUpdateMutation = () =>
  useCreateMutation<void, BulkOperationPayload>(
    productManifestApi.bulkUpdateShelfRowBayBins,
    "Shelf row bay bins bulk updated successfully",
    ["shelfRowBayBins"],
    BulkOperationPayloadSchema
  );

export const useShelfRowFloorCreateMutation = () =>
  useCreateMutation<ShelfRowFloor, CreateShelfRowFloorPayload>(
    productManifestApi.createShelfRowFloor,
    "Shelf row floor created successfully",
    ["shelfRowFloors"],
    CreateShelfRowFloorPayloadSchema
  );

export const useShelfRowFloorCreateFormDataMutation = () =>
  useCreateMutation<ShelfRowFloor, Record<string, any>>(
    (payload: any) => productManifestApi.createShelfRowFloor(payload, true), // Correctly pass the isFormData flag
    "Shelf row floor created successfully (FormData)",
    ["shelfRowFloors"]
  );

export const useFetchShelfRowFloors = (rowId: string) => {
  return useQuery({
    queryKey: ["shelfRowFloors", rowId],
    queryFn: async () => {
      const res = await productManifestApi.getShelfRowFloors(rowId);
      if (!res.success) {
        return [];
      }
      return res.data;
    },
    enabled: !!rowId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useFetchShelfRowFloorById = (id: string) => {
  return useQuery({
    queryKey: ["shelfRowFloor", id],
    queryFn: async () => {
      const res = await productManifestApi.getShelfRowFloorById(id);
      if (!res.success) {
        throw new Error(res.message || "Failed to fetch shelf row floor");
      }
      return res.data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useShelfRowFloorUpdateMutation = (id: string) =>
  useCreateIdBasedMutation<ShelfRowFloor, UpdateShelfRowFloorPayload>(
    productManifestApi.updateShelfRowFloor,
    "Shelf row floor updated successfully",
    ["shelfRowFloors", "shelfRowFloor"],
    UpdateShelfRowFloorPayloadSchema,
    id
  );

export const useShelfRowFloorSlotUpdateMutation = (id: string) =>
  useCreateIdBasedMutation<ShelfRowFloor, UpdateShelfRowFloorPayload>(
    productManifestApi.updateShelfRowFloorSlot,
    "Shelf row floor slot updated successfully",
    ["shelfRowFloors", "shelfRowFloor"],
    UpdateShelfRowFloorPayloadSchema,
    id
  );

export const useShelfRowFloorUpdateFormDataMutation = (id: string) =>
  useCreateIdBasedMutation<ShelfRowFloor, Record<string, any>>(
    (id, payload) =>
      productManifestApi.updateShelfRowFloorFormData(id, payload),
    "Shelf row floor updated successfully (FormData)",
    ["shelfRowFloors", "shelfRowFloor"],
    undefined,
    id
  );

export const useShelfRowFloorDeleteMutation = (id: string) =>
  useCreateIdBasedMutation<void, Record<string, any>>(
    productManifestApi.deleteShelfRowFloor,
    "Shelf row floor deleted successfully",
    ["shelfRowFloors"],
    undefined,
    id
  );

export const useShelfRowFloorBulkUpdateMutation = () =>
  useCreateMutation<void, BulkOperationPayload>(
    productManifestApi.bulkUpdateShelfRowFloors,
    "Shelf row floors bulk updated successfully",
    ["shelfRowFloors"],
    BulkOperationPayloadSchema
  );

export const useShelfRowPalletCreateMutation = () =>
  useCreateMutation<ShelfRowPallet, CreateShelfRowPalletPayload>(
    productManifestApi.createShelfRowPallet,
    "Shelf row pallet created successfully",
    ["shelfRowPallets"],
    CreateShelfRowPalletPayloadSchema
  );

export const useShelfRowPalletCreateFormDataMutation = () =>
  useCreateMutation<ShelfRowPallet, Record<string, any>>(
    (payload: any) => productManifestApi.createShelfRowPallet(payload, true), // Correctly pass the isFormData flag
    "Shelf row pallet created successfully (FormData)",
    ["shelfRowPallets"]
  );

export const useFetchShelfRowPallets = (rowId: string) => {
  return useQuery({
    queryKey: ["shelfRowPallets", rowId],
    queryFn: async () => {
      const res = await productManifestApi.getShelfRowPallets(rowId);
      if (!res.success) {
        return [];
      }
      return res.data;
    },
    enabled: !!rowId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useFetchShelfRowPalletById = (id: string) => {
  return useQuery({
    queryKey: ["shelfRowPallet", id],
    queryFn: async () => {
      const res = await productManifestApi.getShelfRowPalletById(id);
      if (!res.success) {
        throw new Error(res.message || "Failed to fetch shelf row pallet");
      }
      return res.data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useShelfRowPalletSlotUpdateMutation = (id: string) =>
  useCreateIdBasedMutation<ShelfRowPallet, UpdateShelfRowPalletPayload>(
    productManifestApi.updateShelfRowPalletSlot,
    "Shelf row pallet updated successfully",
    ["shelfRowPallets", "shelfRowPallet"],
    UpdateShelfRowPalletPayloadSchema,
    id
  );

export const useShelfRowPalletUpdateMutation = (id: string) =>
  useCreateIdBasedMutation<ShelfRowPallet, UpdateShelfRowPalletPayload>(
    productManifestApi.updateShelfRowPallet,
    "Shelf row pallet updated successfully",
    ["shelfRowPallets", "shelfRowPallet"],
    UpdateShelfRowPalletPayloadSchema,
    id
  );

export const useShelfRowPalletUpdateFormDataMutation = (id: string) =>
  useCreateIdBasedMutation<ShelfRowPallet, Record<string, any>>(
    (id, payload) =>
      productManifestApi.updateShelfRowPalletFormData(id, payload),
    "Shelf row pallet updated successfully (FormData)",
    ["shelfRowPallets", "shelfRowPallet"],
    undefined,
    id
  );

export const useShelfRowPalletDeleteMutation = (id: string) =>
  useCreateIdBasedMutation<void, Record<string, any>>(
    productManifestApi.deleteShelfRowPallet,
    "Shelf row pallet deleted successfully",
    ["shelfRowPallets"],
    undefined,
    id
  );

export const useShelfRowPalletBulkUpdateMutation = () =>
  useCreateMutation<void, BulkOperationPayload>(
    productManifestApi.bulkUpdateShelfRowPallets,
    "Shelf row pallets bulk updated successfully",
    ["shelfRowPallets"],
    BulkOperationPayloadSchema
  );
